import React, {useMemo} from 'react';
import {useAtomValue} from 'jotai';
import {faHome} from '@fortawesome/pro-regular-svg-icons';
import {getCurrentPortalProp, isBrandPortal} from '@netx/core-cms-next/utils';
import {Breadcrumb} from '@netx/core-components/Breadcrumb';
import {
	Breadcrumbs,
	type BreadcrumbsProps,
} from '@netx/core-components/Breadcrumbs';
import {styled} from '@netx/core-components/Theme';
import {useSearchRulesProvider} from '@netx/core-search-rules/contexts/SearchRules';
import {i18n} from '@netx/core/i18n';

const AssetGalleryBreadcrumbsBaseRoot = styled(Breadcrumbs, {
	name: 'AssetGalleryBreadcrumbsBase',
	slot: 'Root',
})(({theme}) => ({}));

const CRUMB_ROOT_HASHES = {
	CATEGORY: [`category/`, `search/in/folder/`],
	SAVED_CART: [`cart-assets/`, `cart`, `search/in/collection/`],
	SEARCH: [
		`search/advanced/`,
		`search/attribute/history/`,
		`search/basic/`,
		`search/color/`,
		`search/exact/`,
		`search/saved/`,
		`search/visual/`,
		`search/in/saved-search/`,
	],
};

type CrumbRootRoute = Record<
	string,
	| {
			hash: string;
			label: string;
	  }
	| undefined
>;

const CRUMB_ROOT_ROUTE: CrumbRootRoute = {
	CATEGORY: isBrandPortal()
		? {
				hash: `#category/${getCurrentPortalProp('category') || 1}`,
				label: i18n.t('i18n.categories'),
		  }
		: undefined,
	SEARCH: isBrandPortal()
		? undefined
		: {
				hash: `#search/saved`,
				label: i18n.t('i18n.searches'),
		  },
	SAVED_CART: {
		hash: `#cart/saved`,
		label: i18n.t('i18n.savedCarts'),
	},
};

const getCrumbRoot = () => {
	const {hash} = window.location;
	let rootRoute: typeof CRUMB_ROOT_ROUTE[keyof typeof CRUMB_ROOT_ROUTE];
	for (const key in CRUMB_ROOT_HASHES) {
		if (
			CRUMB_ROOT_HASHES[key as keyof typeof CRUMB_ROOT_HASHES].findIndex(
				(h: string) => hash.includes(h),
			) < 0
		)
			continue;
		rootRoute = CRUMB_ROOT_ROUTE[key];
		break;
	}
	return rootRoute;
};

export type AssetGalleryBreadcrumbsBaseProps = BreadcrumbsProps;

export const AssetGalleryBreadcrumbsBasePrimitive = ({
	children,
	...props
}: AssetGalleryBreadcrumbsBaseProps) => {
	const {searchRulesAtom} = useSearchRulesProvider();

	const searchRules = useAtomValue(searchRulesAtom);

	const rootRoute = useMemo(() => getCrumbRoot(), [searchRules]);

	return (
		<AssetGalleryBreadcrumbsBaseRoot {...props}>
			<Breadcrumb
				url={'#'}
				label={i18n.t('i18n.home')}
				icon={faHome}
				iconOnly
			/>
			{rootRoute?.label != null ? (
				<Breadcrumb url={rootRoute.hash} label={rootRoute.label} />
			) : null}
			{children}
		</AssetGalleryBreadcrumbsBaseRoot>
	);
};

export const AssetGalleryBreadcrumbsBase = AssetGalleryBreadcrumbsBasePrimitive;

export default AssetGalleryBreadcrumbsBase;
